<template>
  <div class="report">
    <div class="title">
      <div class="district__name">Crosswalking PACER to the IDB</div>
      <div class="title__update">(Updated: {{latest_update}})</div>
    </div>

    <div class="toc">
      <div class="toc__title"> Contents </div>
      <ul>
        <li v-for="el in sectionHeaders">
          <a class="sectionLink" @click="scroll('#'+el.id)"> {{ el.innerHTML }} </a>
        </li>
      </ul>
    </div>




    <h2 id="section1">1. Overview </h2>
    <div class="block">

     The Federal Judicial Center (FJC) provides a database called the <strong>Integrated Data Base (IDB)</strong>
     that contains data on civil and criminal cases. 
     As one of the only comprehensive sources of aggregate data about federal court cases, the IDB is a common starting point for 
     all manner of research into the U.S. judiciary. However, the FJC's data collection and maintenance processes are somewhat opaque, and
     systematic gaps and errors in IDB data often plague legal scholars' attempts at
     large-scale research work.<span class='citation'><a class="sectionLink" @click="scroll('#footnotes')">[1]</a></span>
     <br/><br/>
    
     In the viewer below, you can browse a sample row of the IDB's civil data table. (The IDB civil codebook can be found
     <a href="https://www.fjc.gov/sites/default/files/idb/codebooks/Civil%20Codebook%201988%20Forward.pdf">here</a>.)

     <div class="jsonviewer">
        <json-viewer :value="jsonData1"></json-viewer>
     </div>
     <br/>

     Another abundant source of federal court data is the federal government's
     <strong>Public Access to Electronic Court Records (PACER)</strong> case-tracking software, used by litigants and researchers alike.
     PACER docket sheets, which are HTML tables describing the parties to a case and the documents filed over its lifetime,
     are one of the most fine-grained and accurate data sources available to the legal research community.
     Unfortunately, PACER keeps all its docket-level data behind a paywall, so collecting enough docket sheets to make
     inferences about trends in the courts can be a prohibitively expensive undertaking.
     <br/><br/>

     The SCALES team relies on PACER dockets - parsed from HTMLs to JSONs using our open-source 
     <a href="https://github.com/scales-okn/infrastructure_dev/tree/master/code/parsers">PACER parser</a> - as the 
     primary data source for Satyrn, our research interface, as well as for internal research work.
      <br/><br/>

      In the viewer below, you can browse a sample JSON file from the SCALES database. (A schema file describing the SCALES civil data structure
      can be found <a href='https://github.com/scales-okn/infrastructure_dev/blob/master/code/parsers/schemas/case_cv_v1.schema.json'>
      here</a>.)

     <div class="jsonviewer">
        <json-viewer :value="jsonData2"></json-viewer>
     </div>

     There are pros and cons to both the IDB and PACER, and certain research questions may be better suited to one or the other. 
     To better understand the relationship between these data sources, as well as to quantify their potential shortcomings, 
     the SCALES team has crosswalked the PACER dockets in our database to their corresponding IDB entries.

    </div>




    <h2 id="section2"> 2. Methodology </h2>
    <div class="block">

      <h3 id="section2.1"> 2.1 Data </h3>
      The crosswalk described in this report uses data from the following sources:
      <ul>
        <li> <a href="https://pacer.uscourts.gov">PACER.gov:</a> civil docket sheets for cases filed in 2016 and 2017 (all districts)</li>
        <li> <a href="https://www.fjc.gov/research/idb">FJC Integrated Database:</a> civil case data for cases filed in fiscal years 2010
          through 2019 (all districts)</li>
      </ul>

      Two features of the data merit further explanation.
      First, the IDB's data structure diverges in its representations of civil and criminal cases.
      Civil cases appear only once in the IDB, while criminal cases appear as defendant-level panel data
      (that is, each defendant on a criminal case gets a separate row in the IDB for each year that the case is pending).
      In order to preserve a one-to-one relationship between PACER dockets and IDB rows —
      thereby streamlining our crosswalking algorithm and simplifying our results —
      we have confined our analysis to civil litigation.
      <br/><br/>

      Second, PACER and the IDB occasionally report different filing dates for the same case.
      This fact does not restrict our ability to crosswalk 2016-2017 PACER cases to the IDB,
      since we can search through many years' worth of IDB files for any cases that do not appear in the IDB's 2016-2017 data.
      To crosswalk 2016-2017 IDB cases back to PACER, though
      (as would be necessary for a truly exhaustive survey of the data sources' relative strengths and weaknesses),
      we would require many more than two years of PACER data — a prohibitively expensive proposition on account of PACER's paywall.
      As a result, we have performed a unidirectional crosswalk only, from PACER to the IDB.
      <br/><br/>

      However, running the reverse crosswalk in the Northern District of Illinois
      (for which we have longitudinal data reaching back to 2002)
      reveals that every one of the 21,762 IDB cases listed as having been filed in 2016 or 2017 can also be found in PACER.
      Thus, we can reasonably assume that most mismatches between the IDB and PACER
      will take the form of holes in the IDB rather than holes in PACER,
      meaning that our unidirectional crosswalk will capture the majority of the non-overlap between the two data sources.
      <!-- unidirectional; non-exhaustive; data limitation / no misses among 27162 cases; vast majority of non-overlap -->
      <br/><br/>

      <h3 id="section2.2"> 2.2 Analysis </h3>
      To crosswalk the datasets, we combined several IDB columns to deduce a PACER case docket number for each case in the IDB.
      In section 3.1, we examine the subset of PACER cases for which no corresponding rows in the IDB could be found.
      <br/><br/>

      <!-- TECHNICAL NOTE: crosswalking algorithm) -->

      Finally, as an additional check on the data, we used a SCALES classifier to determine whether each PACER docket contained any
      <em>in forma pauperis</em> activity (i.e., applications for fee waivers and/or grants & denials of those waivers),
      and we compared these results to the IDB's 'IFP' column. This analysis is presented in section 3.2.

    </div>




    <h2 id="section3"> 3. Results </h2>
    <h3 id="section3.1"> 3.1 Gaps in the IDB </h3>
    <div class="block">

      Our PACER-IDB crosswalk reveals a minor yet pervasive incompleteness in the IDB's case records.
      Overall, <strong>1.5%</strong> of PACER cases within the scope of our crosswalk (<strong>8,065</strong> cases) could not be found in the IDB.
      <br/><br/>

      Figure 1 below gives a more specific accounting of this incompleteness.
      Each cell in the figure corresponds to a specific federal court district and nature of suit subtype.
      For instance, consulting the cell with coordinates <em>&ltcaed, Habeas Corpus&gt</em> reveals that
      6.6% of habeas corpus cases in the Eastern District of California could not be located in the IDB.
      (You can also use the dropdown below the figure to toggle additional views of the data.)
      <br/><br/>

    </div>

    <div class="facetgrid">
    <div class="facetgrid__row">

      <Plot v-bind="heatmap1" v-if="heatmap1" :key="'heatmap1'" :width="1100" :height="500" />
      <Loader v-else :width="450" :height="500" />

    </div>
    <div class="facetgrid__title"> Figure 1 </div>
    </div>

    <div class="controlPanel controlPanel--centered">
    <md-field class="inline" style="width:250px">
      <label for="option-select"> Select a plot: </label>
      <md-select v-model="heatmap1Active" name="option-select">
        <md-option v-for="option in Object.keys(heatmap1OptionNames)" v-bind:value="option">
          {{ heatmap1OptionNames[option] }}
        </md-option>
      </md-select>
    </md-field>
    </div>

    <div class="block">

      <!-- TECHNICAL NOTE: rows ordered by frequency, cell coloring capped at 30% -->

      This breakdown reveals that the IDB's incompleteness is more closely correlated to district than to nature of suit,
      as hinted at by the vertical bands of color running up and down the heatmap.
      For example, holes in the IDB's coverage are especially prominent in the Southern District of Florida,
      where some <strong>11.2%</strong> of PACER cases had no matching rows in the IDB.

    </div><br/>




    <h3 id="section3.2"> 3.2 Case study: <em>In forma pauperis</em> activity </h3>
    <div class="block">

      Even when PACER and the IDB agree on case counts, the IDB still tends to be ill-suited to certain domains of legal research.
      One such domain is <em>in forma pauperis</em> litigation; according to our survey of the PACER dockets that correspond to cases in the IDB,
      around <strong>8.5%</strong> of the values in the IDB's boolean 'ifp' column (<strong>46,081</strong> cases) are incorrect.
      <br/><br/>

      Figure 2 breaks down these results by district and nature of suit.
      Additionally, you can use the dropdown to distinguish between cases where the IDB value was incorrectly 'False'
      (i.e., where the IDB overlooked <em>in forma pauperis</em> filings that appear in PACER)
      and cases where the value was incorrectly 'True'
      (i.e., where the IDB reported <em>in forma pauperis</em> activity that could not be found in the docket).
      <br/><br/>

      <!-- TECHNICAL NOTE: ifp detection algorithm -->

    </div>

    <div class="facetgrid">
    <div class="facetgrid__row">

      <Plot v-bind="heatmap2" v-if="heatmap2" :key="'heatmap2'" :width="1100" :height="500" />
      <Loader v-else :width="450" :height="500" />

    </div>
    <div class="facetgrid__title"> Figure 2 </div>
    </div>

    <div class="controlPanel controlPanel--centered">
    <md-field class="inline" style="width:170px">
      <label for="option-select"> Select a plot: </label>
      <md-select v-model="heatmap2Active" name="option-select">
        <md-option v-for="option in Object.keys(heatmap2OptionNames)" v-bind:value="option">
          {{ heatmap2OptionNames[option] }}
        </md-option>
      </md-select>
    </md-field>
    </div>

    <div class="block">

      Note that, unlike in the previous figure, the bands of color in this heatmap tend to run side to side,
      suggesting that errors in the 'ifp' column are more strongly correlated with nature of suit than with district.
      In particular, the IDB's <em>in forma pauperis</em> flags for habeas corpus cases diverge sharply
      from PACER's records of <em>in forma pauperis</em> activity in those cases,
      with a <strong>33.4%</strong> composite error rate across all districts.
      <br/><br/>

      What accounts for these extensive inaccuracies? The FJC sheds some light on the issue in its recent<br/>
      <a href='https://www.fjc.gov/sites/default/files/IDB-Research-Guide.pdf'>IDB Research Guide</a> (emphasis added):

      <blockquote><em>
        While many of the fields, especially with respect to those values in the published Judicial Business
        Reports, are collected with data quality control measures in place, some are not. A process of
        error correction and detection does not cover all the variables in the IDB, though both the FJC
        and the AO make every effort to ensure the accuracy of the data. Nonetheless, <strong>there may be
        some problems with specific fields that are not routinely reported.</strong>
        <br/><br/>

        The two issues with respect to data collection noted above are <strong>more likely to affect
        specific fields related to under-served populations.</strong> These fields include information regarding
        pro se litigants, in forma pauperis (IFP) status, and class action allegations. All three fields may
        change over the life of the case, making the issues with record replacement more pronounced in
        these fields, and all three are without data quality control checks, making it more likely that there
        are errors in reporting at any point across the duration of the case.
      </em></blockquote>

      The FJC makes a fair point: to some extent, the quality of the IDB hinges on the diligence of the ninety-four district courts
      in reporting their case statistics to the AOUSC each quarter.
      Indeed, given the challenges inherent in collating so many disparate streams of data,
      the very existence of a unified, up-to-date, publicly available database of court filings is an impressive accomplishment.
      When taking advantage of all the IDB has to offer, though, the legal research community should take care to remember what it cannot offer:
      comprehensive, detailed, reliable data of the sort that only court records themselves can provide.
      <br/><br/>

    </div>

    <h3 id='footnotes'>Footnotes</h3>
    <div class="block">
      [1] <a href='https://openscholarship.wustl.edu/cgi/viewcontent.cgi?article=1123&context=law_journal_law_policy' target='_blank'>
        PT Kim, M Schlanger, CL Boyd, AD Martin. (2009) How should we study district judge decision-making?
        <em>Wash. UJL & Pol'y</em> 29, 83-112.
      </a>
      <br/><br/>
    </div>

    <div class="infoBox">
      For more information about this report, or to request further analysis of data from a specific district or time frame,
      please feel free to reach out to the SCALES OKN team at <a href="mailto:scales-okn@northwestern.edu">scales-okn@northwestern.edu</a>.
    </div>

  </div>
</template>




<script>

import JsonViewer from 'vue-json-viewer'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import api from '@/api.js'
import court2circuit from '@/data/court2circuit.js'

// Components
import Plot from '@/components/Plot.vue'
import Loader from '@/components/Loader.vue'
import DocketSnippet from '@/components/DocketSnippet'
import _merge from 'lodash/merge'
import _cloneDeep from 'lodash/cloneDeep'
import {graphFactory} from '@/tools.js'


export default {
  name: 'Report_sealing',
  components: {
    Plot,
    DocketSnippet,
    JsonViewer,
    PulseLoader,
    Loader
  },
  props: {
    reportName: String,
  },
  data: function(){
    return {

      heatmap1OptionNames: {'pcts': 'Percentage missing', 'counts': 'Number of cases missing', 'caseloads': 'Total caseloads'},
      heatmap1Titles: {'pcts': "Percentage of PACER cases missing from the IDB", 'counts': "Number of PACER cases missing from the IDB", 'caseloads': "Total number of PACER cases"},
      heatmap1TitlePadding: {'pcts': -227, 'counts': -215, 'caseloads': -120},
      heatmap1Active: 'pcts',

      heatmap2OptionNames: {'ifp_neg': 'Overlooked IFP', 'ifp_pos': 'Nonexistent IFP'},
      heatmap2Titles: {'ifp_neg': "Percentage of IDB rows that overlook IFP activity found in PACER", 'ifp_pos': "Percentage of IDB rows that report IFP activity not found in PACER"},
      heatmap2TitlePadding: {'ifp_neg': -320, 'ifp_pos': -327},
      heatmap2Active: 'ifp_neg',

      jsonData1: {
        'circuit': '8', 'district': 'ared', 'office': '4', 'docket': '1600753', 'origin': '1', 'filedate': '2016-10-17', 'fdateuse': '2016-10-01', 'juris': '3', 'nos': '555', 'titl': 'NaN', 'section': '1983', 'subsect': 'PR', 'residenc': '-8', 'jury': 'N', 'classact': '-8', 'demanded': '0', 'filejudg': 'NaN', 'filemag': 'NaN', 'county': '5115', 'arbit': '-8', 'mdldock': '-8', 'plt': 'WRIGHT', 'def': 'JONES, ET AL', 'transdat': 'NaN', 'transoff': '-8', 'transdoc': '-8', 'transorg': '-8', 'termdate': '2017-06-27', 'tdateuse': '2017-06-01', 'trclact': '-8', 'termjudg': 'NaN', 'termmag': 'NaN', 'procprog': '4', 'disp': '6', 'noj': '0', 'amtrec': '0', 'judgment': '2', 'djoined': '2016-11-02', 'pretrial': 'NaN', 'tribegan': 'NaN', 'trialend': 'NaN', 'trmarb': '-8', 'prose': '1', 'ifp': 'FP', 'statuscd': 'L', 'tapeyear': '2017'
      },
      jsonData2: {
        "case_id": "4:16-cv-00753", "case_type": "cv", "court": "ared", "ucid": "ared;;4:16-cv-00753", "city": "Central Division", "header_case_id": "4:16-cv-00753-KGB", "filing_date": "10/17/2016", "terminating_date": "06/27/2017", "case_status": "closed", "nature_suit": "555 Prison Condition", "jury_demand": "None", "cause": "42:1983 Prisoner Civil Rights", "jurisdiction": "Federal Question", "monetary_demand": null, "lead_case_pacer_id": null, "lead_case_id": null, "related_cases": [], "other_courts": [], "magistrate_case_ids": [], "filed_in_error_text": null, "case_flags": ["CLOSED", "RD"], "judge": "Judge Kristine G. Baker", "referred_judges": [], "appeals_case_ids": [], "parties": [{"name": "Robert Alan Wright", "entity_info": {"office_name": null, "address": null, "phone": null, "fax": null, "email": null, "terminating_date": null, "raw_info": "<i>ADC #657408</i>"}, "counsel": [{"name": "Robert Alan Wright", "entity_info": {"office_name": null, "address": "ADC #657408\nOUACHITA RIVER UNIT\nArkansas Division of Correction\nPost Office Box 1630\nMalvern, AR 72104", "phone": null, "fax": null, "email": null, "terminating_date": null, "raw_info": "ADC #657408\n<br/>OUACHITA RIVER UNIT\n<br/>Arkansas Division of Correction\n<br/>Post Office Box 1630\n<br/>Malvern, AR 72104\n<br/>PRO SE"}, "is_pro_se": true, "is_lead_attorney": false, "is_notice_attorney": false, "is_pro_hac_vice": false, "has_see_above": false, "designation": null, "bar_status": null, "trial_bar_status": null}], "role": "Plaintiff", "party_type": "plaintiff", "pacer_id": null}, {"name": "Shane Jones", "entity_info": {"office_name": null, "address": null, "phone": null, "fax": null, "email": null, "terminating_date": null, "raw_info": "<i>Sheriff, Pope County</i>"}, "counsel": [{"name": "John R. Myers", "entity_info": {"office_name": "Attorney at Law", "address": "The Prospect Building, Suite 230\n1501 North University Avenue\nLittle Rock, AR 72207", "phone": "501-663-4700", "fax": null, "email": "johnmyerslawfirm@sbcglobal.net", "terminating_date": null, "raw_info": "Attorney at Law \n<br/>The Prospect Building, Suite 230 \n<br/>1501 North University Avenue \n<br/>Little Rock, AR 72207 \n<br/>501-663-4700 \n<br/>Email: johnmyerslawfirm@sbcglobal.net\n<br/><i>LEAD ATTORNEY</i>\n<br/><i>ATTORNEY TO BE NOTICED</i>"}, "is_pro_se": false, "is_lead_attorney": true, "is_notice_attorney": true, "is_pro_hac_vice": false, "has_see_above": false, "designation": null, "bar_status": null, "trial_bar_status": null}, {"name": "C. Burt Newell", "entity_info": {"office_name": "C. Burt Newell, Attorney at Law", "address": "Post Office Box 1620\nHot Springs, AR 71902-1620", "phone": "501-321-2222", "fax": "501-624-0533", "email": "burt@hotspringslaw.net", "terminating_date": null, "raw_info": "C. Burt Newell, Attorney at Law \n<br/>Post Office Box 1620 \n<br/>Hot Springs, AR 71902-1620 \n<br/>501-321-2222 \n<br/>Fax: 501-624-0533 \n<br/>Email: burt@hotspringslaw.net\n<br/><i>ATTORNEY TO BE NOTICED</i>"}, "is_pro_se": false, "is_lead_attorney": false, "is_notice_attorney": true, "is_pro_hac_vice": false, "has_see_above": false, "designation": null, "bar_status": null, "trial_bar_status": null}], "role": "Defendant", "party_type": "defendant", "pacer_id": null}, {"name": "Rowdy Sweet", "entity_info": {"office_name": null, "address": null, "phone": null, "fax": null, "email": null, "terminating_date": null, "raw_info": "<i>Captain, Pope County Detention Center</i>"}, "counsel": [{"name": "John R. Myers", "entity_info": {"office_name": null, "address": null, "phone": null, "fax": null, "email": null, "terminating_date": null, "raw_info": "(See above for address)\n<br/><i>LEAD ATTORNEY</i>\n<br/><i>ATTORNEY TO BE NOTICED</i>"}, "is_pro_se": false, "is_lead_attorney": true, "is_notice_attorney": true, "is_pro_hac_vice": false, "has_see_above": true, "designation": null, "bar_status": null, "trial_bar_status": null}, {"name": "C. Burt Newell", "entity_info": {"office_name": null, "address": null, "phone": null, "fax": null, "email": null, "terminating_date": null, "raw_info": "(See above for address)\n<br/><i>ATTORNEY TO BE NOTICED</i>"}, "is_pro_se": false, "is_lead_attorney": false, "is_notice_attorney": true, "is_pro_hac_vice": false, "has_see_above": true, "designation": null, "bar_status": null, "trial_bar_status": null}], "role": "Defendant", "party_type": "defendant", "pacer_id": null}], "case_name": "Wright v. Jones et al", "docket": [{"date_filed": "10/17/2016", "ind": "1", "docket_text": "MOTION for Leave to Proceed in forma pauperis by Robert Alan Wright. (ljb) (Entered: 10/18/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713902763", "span": {}}}, "edges": []}, {"date_filed": "10/17/2016", "ind": "2", "docket_text": "COMPLAINT against All Defendants filed by Robert Alan Wright. (ljb) (Entered: 10/18/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713902778", "span": {}}}, "edges": []}, {"date_filed": "10/19/2016", "ind": "3", "docket_text": "ORDER granting Plaintiff's Application to Proceed Without Prepayment of Fees and Affidavit 1 . Signed by Magistrate Judge Joe J. Volpe on 10/19/2016. (lej) (Entered: 10/19/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713904812", "span": {}}}, "edges": [[2, 0, {"start": 91, "end": 92}]]}, {"date_filed": "10/19/2016", "ind": "4", "docket_text": "ORDER directing the Clerk to prepare summons and the USM to serve Defendants without prepayment of fees and costs or security therefore. Signed by Magistrate Judge Joe J. Volpe on 10/19/2016. (lej) (Entered: 10/19/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713904820", "span": {}}}, "edges": []}, {"date_filed": "10/19/2016", "ind": null, "docket_text": "Summons Issued as to All Defendants. (lej) (Entered: 10/19/2016)", "documents": {}, "edges": []}, {"date_filed": "10/28/2016", "ind": "5", "docket_text": "Mail Returned Undeliverable as to Robert Alan Wright re 3 4 Orders. (rhm) (Entered: 10/28/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713911131", "span": {}}}, "edges": [[5, 2, {"start": 56, "end": 57}], [5, 3, {"start": 58, "end": 59}]]}, {"date_filed": "10/28/2016", "ind": "6", "docket_text": "ORDER directing Plaintiff to comply with LR 5.5(c)(2) within 30 days of the entry of this order or this case will be recommended for dismissal. Signed by Magistrate Judge Joe J. Volpe on 10/28/2016. (lej) (Entered: 10/28/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713911367", "span": {}}}, "edges": []}, {"date_filed": "11/02/2016", "ind": "7", "docket_text": "ANSWER to 2 Complaint by Shane Jones, Rowdy Sweet.(Myers, John) (Entered: 11/02/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713914401", "span": {}}}, "edges": [[7, 1, {"start": 10, "end": 11}]]}, {"date_filed": "11/02/2016", "ind": "8", "docket_text": "NOTICE of Appearance by C. Burt Newell on behalf of Shane Jones, Rowdy Sweet (Newell, C.) (Entered: 11/02/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713914410", "span": {}}}, "edges": []}, {"date_filed": "11/02/2016", "ind": "9", "docket_text": "NOTICE, CONSENT AND REFERENCE to a Magistrate Judge forwarded by the Clerk of Court. (lej) (Entered: 11/02/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713914587", "span": {}}}, "edges": []}, {"date_filed": "11/02/2016", "ind": "10", "docket_text": "INITIAL SCHEDULING ORDER: Discovery due by 5/2/2017. Dispositive motions due by 6/2/2017. Dispositive motions on the issue of exhaustion due by 1/3/2017. Signed by Magistrate Judge Joe J. Volpe on 11/2/2016. (lej) (Entered: 11/02/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713914596", "span": {}}}, "edges": []}, {"date_filed": "11/02/2016", "ind": "11", "docket_text": "NOTICE of Change of Address by Robert Alan Wright. (thd) (Entered: 11/02/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713915166", "span": {}}}, "edges": []}, {"date_filed": "11/04/2016", "ind": "12", "docket_text": "Mail Returned Undeliverable as to Robert Alan Wright re 6 Order. (rhm) (Entered: 11/04/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713916543", "span": {}}}, "edges": [[12, 6, {"start": 56, "end": 57}]]}, {"date_filed": "11/04/2016", "ind": "13", "docket_text": "SUMMONS Returned Executed. Shane Jones served on 11/3/2016. (fjg) (Entered: 11/04/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713917044", "span": {}}}, "edges": []}, {"date_filed": "11/04/2016", "ind": "14", "docket_text": "SUMMONS Returned Executed. Rowdy Sweet served on 11/3/2016. (fjg) (Entered: 11/04/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713917053", "span": {}}}, "edges": []}, {"date_filed": "11/09/2016", "ind": "15", "docket_text": "Mail Returned Undeliverable as to Robert Alan Wright re 9 Consent Notice and 10 Scheduling Order. Previously resent to updated address. (rhm) (Entered: 11/09/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713919612", "span": {}}}, "edges": [[15, 9, {"start": 56, "end": 57}], [15, 10, {"start": 77, "end": 79}]]}, {"date_filed": "12/13/2016", "ind": "16", "docket_text": "Mail Returned Undeliverable as to Robert Alan Wright re 1 Motion for Leave to Proceed in forma pauperis and 2 Complaint. Remailed to updated address. (rhm) (Entered: 12/13/2016)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713942114", "span": {}}}, "edges": [[16, 0, {"start": 56, "end": 57}], [16, 1, {"start": 108, "end": 109}]]}, {"date_filed": "01/27/2017", "ind": "17", "docket_text": "MOTION to Compel by Shane Jones, Rowdy Sweet (Attachments: # 1 Exhibit A - November 8, 2016 Letter to Plaintiff, # 2 Exhibit B - December 16, 2016 Letter to Plaintiff)(Myers, John) (Entered: 01/27/2017)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02703970607", "span": {}}, "1": {"url": "https://ecf.ared.uscourts.gov/doc1/02713970608", "span": {"start": 61, "end": 62}}, "2": {"url": "https://ecf.ared.uscourts.gov/doc1/02713970609", "span": {"start": 115, "end": 116}}}, "edges": []}, {"date_filed": "01/27/2017", "ind": "18", "docket_text": "ORDER granting Defendants' motion to compel 17 . Plaintiff is directed to respond to Defendants' discovery requests within 14 days of the entry of this order. Signed by Magistrate Judge Joe J. Volpe on 1/27/2017. (lej) (Entered: 01/27/2017)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713970939", "span": {}}}, "edges": [[18, 17, {"start": 44, "end": 46}]]}, {"date_filed": "02/21/2017", "ind": "19", "docket_text": "MOTION to Dismiss with Incorporated Brief by Shane Jones, Rowdy Sweet (Myers, John) (Docket text modified on 2/22/2017 to correct the description of the document filed). (jak) (Entered: 02/21/2017)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713988164", "span": {}}}, "edges": []}, {"date_filed": "02/22/2017", "ind": "20", "docket_text": "PROPOSED FINDINGS AND RECOMMENDATIONS that Defendant's motion to dismiss be granted 19 . Plaintiff's complaint be dismissed without prejudice 2 . The Court certify, pursuant to 28 U.S.C. \u00a7 1915(a)(3), that an in forma pauperis appeal from any order adopting these recommendations would not be taken in good faith. Objections due no later than 14 days from the date of the findings and recommendations. Signed by Magistrate Judge Joe J. Volpe on 2/22/2017. (lej) (Entered: 02/22/2017)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02713988557", "span": {}}}, "edges": [[20, 19, {"start": 84, "end": 86}], [20, 1, {"start": 142, "end": 143}]]}, {"date_filed": "02/22/2017", "ind": null, "docket_text": "NOTICE OF DOCKET CORRECTION re 19 MOTION to Dismiss. CORRECTION: The docket text was modified to correct the description of the document filed as \"MOTION to Dismiss with Incorporated Brief by Shane Jones, Rowdy Sweet\" as marked on the document.(jak) (Entered: 02/22/2017)", "documents": {}, "edges": [[21, 19, {"start": 31, "end": 33}]]}, {"date_filed": "06/27/2017", "ind": "21", "docket_text": "ORDER approving and adopting 20 Proposed Findings and Recommendations in all respects; granting 19 defendants' motion to dismiss; dismissing this action without prejudice; and certifying than an in forma pauperis appeal from this Order would not be taken in good faith. Signed by Judge Kristine G. Baker on 6/27/2017. (thd) (Entered: 06/27/2017)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02714083219", "span": {}}}, "edges": [[22, 20, {"start": 29, "end": 31}], [22, 19, {"start": 96, "end": 98}]]}, {"date_filed": "06/27/2017", "ind": "22", "docket_text": "JUDGMENT: Consistent with 21 Order, this case is dismissed without prejudice. Signed by Judge Kristine G. Baker on 6/27/2017. (thd) (Entered: 06/27/2017)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02714083225", "span": {}}}, "edges": [[23, 22, {"start": 26, "end": 28}]]}, {"date_filed": "06/27/2017", "ind": "23", "docket_text": "APPELLATE FILING FEE NOTICE to Plaintiff. (thd) (Entered: 06/27/2017)", "documents": {"0": {"url": "https://ecf.ared.uscourts.gov/doc1/02714083242", "span": {}}}, "edges": []}], "docket_available": true, "member_case_key": null, "mdl_code": null, "mdl_id_source": null, "is_mdl": false, "is_multi": false, "billable_pages": 3, "cost": 0.3, "download_timestamp": "10/06/2021 22:38:38", "n_docket_reports": 1, "source": "pacer", "recap_id": null, "download_url": "https://ecf.ared.uscourts.gov/cgi-bin/DktRpt.pl?124846798757632-L_1_0-1", "case_pacer_id": "105358", "stub": false
      },

      pageReady: false,
      ready: false,
      sectionHeaders: null,
      baseBlue: "#448aff",
      latest_update: '11/30/2021',

    }
  },


  computed: {

    heatmap1: function(){
      return graphFactory(this.$store.state.graphs, '1', 'heatmap',
        this.heatmap1Active,
        {'title': {'pad': {'l': this.heatmap1TitlePadding[this.heatmap1Active]}}},
        ()=>this.heatmap1Titles[this.heatmap1Active])
    },

    heatmap2: function(){
      return graphFactory(this.$store.state.graphs, '1', 'heatmap',
        this.heatmap2Active,
        {'title': {'pad': {'l': this.heatmap2TitlePadding[this.heatmap2Active]}}},
        ()=>this.heatmap2Titles[this.heatmap2Active])
    },

  },


  methods:{

    scroll(selector){
      const el = document.querySelector(selector)

      if (el) {
        el.scrollIntoView({behavior: 'smooth'})
      }
    },

    getSectionHeaders(){
      const sel = 'h1:not([tocExclude]),h2:not([tocExclude])'
      return Array.from(this.$el.querySelectorAll(sel))
    },

  },

  created(){

  },

  mounted(){
    this.pageReady = true
    this.sectionHeaders = this.getSectionHeaders()

    this.$store.dispatch('getHeatmap', {
      reportName: this.reportName
    })

  }
}

</script>

<style>

.title__update {
  margin-top: 15px;
}

.citation {
  vertical-align: top;
  font-size: 0.6em;
}

.jsonviewer {
  margin-left: 60px;
  margin-top: 20px;
  margin-bottom: 40px;
  height: 450px;
  width: 1015px;

  overflow: auto;
  border: 1px solid #ccc;
}

.facetgrid {
  margin-top: 0px;
}

strong {
  font-weight: 400;
}

</style>
